import { PureQueryOptions } from '@apollo/client';
import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { ActorsWithFiltersDocument } from '@avatar/graphql/filterAvatars/operation.generated';
import { useCanUseTeamView } from 'abilities/team';
import { UserDrivesDocument } from 'components/Drives/operation.generated';
import Prompts from '../../context/prompts';
import { usePromptsContext } from '../../context/prompts/PromptsContext';
import {
  useUserSubscriptionPlans,
  UserSubscriptionPlansDocument,
} from './operation.generated';
import { useIsManagedUser } from '../../context/user/UserContext';
import { UserContextDocument } from '../../context/user/operation.generated';
import { ProductUsageDocument } from '../ProductUsage/operation.generated';
import { AvailableAddOnPlansQueryDocument } from '../Subscriptions/AddOn/hooks/operation.generated';
import { toast } from '../Toaster';

type UpgradeOptions = Pick<
  Prompts.OpenSubscriptionTiersPrompt['payload'],
  'upgradeContext' | 'upgradePath'
>;

export const subscriptionUpgradeRefetchQueries: PureQueryOptions[] = [
  { query: ActorsWithFiltersDocument, variables: { inputs: {} } },
  { query: UserSubscriptionPlansDocument },
  { query: UserContextDocument },
  { query: UserDrivesDocument, variables: { inputs: {} } },
  { query: ProductUsageDocument },
  { query: AvailableAddOnPlansQueryDocument },
];

/**
 * Small hook containing all the data needed to properly open an upgrade-subscription modal
 */
export default function usePromptForSubscriptionUpgrade({
  upgradePath: path,
  upgradeContext: context,
}: UpgradeOptions): [(inputs?: { extraUpgradePathParams?: string }) => void] {
  const router = useRouter();
  const isManagedUser = useIsManagedUser();
  const [, promptsDispatch] = usePromptsContext();
  const { data: subscriptionPlansData } = useUserSubscriptionPlans({
    variables: {
      inputs: {
        via: router?.query.via as string,
      },
    },
  });
  const canUseTeamView = useCanUseTeamView();

  const action = useCallback(
    (inputs?: { extraUpgradePathParams?: string }) => {
      const upgradePath = inputs?.extraUpgradePathParams
        ? `${path} > ${inputs.extraUpgradePathParams}`
        : path;

      if (isManagedUser) {
        return;
      }

      if (canUseTeamView) {
        promptsDispatch({
          type: 'OpenSubscriptionTiersPrompt',
          payload: {
            upgradeContext: 'team_upgrade',
            upgradePath,
          },
        });
      } else if (subscriptionPlansData?.user.subscriptionPlans.length) {
        promptsDispatch({
          type: 'OpenSubscriptionTiersPrompt',
          payload: {
            upgradeContext: context,
            upgradeAction: 'upgrade',
            subscriptionPlansSelectorProps: {
              plans: subscriptionPlansData.user.subscriptionPlans,
            },
            upgradePath,
          },
        });
      } else {
        // this shouldn't happen, but adding this just in case.
        toast({ message: `Please try again in a few minutes.`, type: 'error' });
      }
    },
    [
      // props are deconstructed on purpose, since passing the props object would nullify this
      // useCallback
      path,
      context,
      subscriptionPlansData,
      promptsDispatch,
      canUseTeamView,
      isManagedUser,
    ]
  );

  return [action];
}
